import { useEffect, useRef, useState } from "react";
import { Zap, AlertTriangle, Target, Flag } from "lucide-react";
import { trackEvent } from "@/lib/audit";
import { useMode } from "@/lib/mode";
import { useLanguage } from "@/hooks/useLanguage";
import { useDelayedBySignal } from "@/lib/sync/offsetOrchestration";

interface FrameLike {
  ball: { x: number; y: number };
  inDanger: boolean;
  freeKick?: boolean;
  players: Array<{ sprinting?: boolean; team: "MEX" | "ARG" }>;
  momentum?: number;
}

type AlertKind = "danger" | "box" | "xg" | "sprint" | "freekick";

interface EdgeAlert {
  id: string;
  kind: AlertKind;
  label: string;
  detail: string;
}

const KEY_BY_KIND: Record<AlertKind, { label: string; detail: string }> = {
  danger: { label: "alert_danger", detail: "alert_danger_d" },
  box: { label: "alert_box", detail: "alert_box_d" },
  xg: { label: "alert_xg", detail: "alert_xg_d" },
  sprint: { label: "alert_sprint", detail: "alert_sprint_d" },
  freekick: { label: "alert_freekick", detail: "alert_freekick_d" },
};

export function EdgeAlerts({ frame, xgPct }: { frame: FrameLike; xgPct: number }) {
  const [alerts, setAlerts] = useState<EdgeAlert[]>([]);
  const [mode] = useMode();
  const { t } = useLanguage();
  const lastByKind = useRef<Record<AlertKind, number>>({
    danger: 0, box: 0, xg: 0, sprint: 0, freekick: 0,
  });

  useEffect(() => {
    const now = Date.now();
    const fire = (kind: AlertKind) => {
      if (now - lastByKind.current[kind] < 6000) return;
      lastByKind.current[kind] = now;
      const keys = KEY_BY_KIND[kind];
      const a: EdgeAlert = { id: `${kind}-${now}`, kind, label: t(keys.label), detail: t(keys.detail) };
      setAlerts((prev) => [...prev.slice(-2), a]);
      void trackEvent("edge_alert_triggered", { kind, mode });
      void trackEvent("edge_alert_viewed", { kind, mode });
      // Optional vibration
      try {
        if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(30);
      } catch { /* noop */ }
      window.setTimeout(() => {
        setAlerts((prev) => prev.filter((x) => x.id !== a.id));
      }, 3000);
    };

    if (xgPct >= 0.6) fire("xg");
    else if (frame.ball.x > 32 && Math.abs(frame.ball.y) < 20) fire("box");
    else if (frame.ball.x > 16) fire("danger");
    if (frame.players.some((p) => p.sprinting)) fire("sprint");
    if (frame.freeKick && frame.ball.x > 20) fire("freekick");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frame.ball.x, frame.ball.y, frame.inDanger, frame.freeKick, xgPct]);

  // Phase 1 — radar alerts use the radar_alert weight (heavy but not full
  // spoiler-safe; goals still own that tier).
  const delayedAlerts = useDelayedBySignal(alerts, "radar_alert");
  if (delayedAlerts.length === 0) return null;

  const iconFor = (k: AlertKind) =>
    k === "xg" ? Target : k === "freekick" ? Flag : k === "sprint" ? Zap : AlertTriangle;

  return (
    <div className="pointer-events-none absolute left-1/2 bottom-3 z-20 -translate-x-1/2 space-y-1.5 max-w-[92%] sm:top-14 sm:bottom-auto">
      {delayedAlerts.map((a) => {
        const Icon = iconFor(a.kind);
        return (
          <button
            key={a.id}
            onClick={() => void trackEvent("edge_alert_clicked", { kind: a.kind })}
            className="pointer-events-auto flex items-center gap-2 rounded-full border border-primary/50 bg-background/85 px-3 py-1.5 text-[11px] font-extrabold shadow-2xl backdrop-blur-md animate-fade-in"
            style={{ color: "oklch(0.7 0.22 27)" }}
          >
            <Icon className="h-3.5 w-3.5" />
            <span>{a.label}</span>
            <span className="hidden sm:inline text-[10px] font-semibold text-muted-foreground">
              · {a.detail}
            </span>
          </button>
        );
      })}
    </div>
  );
}